import { useEffect, useState, useRef } from "react";

import { TryOnScene } from "../../tryon/TryOnScene";
import { useElementSize } from "../../tryon/use-element-size";
import { useWebcamTracking } from "../../tryon/use-webcam-tracking";
import { useDispatch, useSelector } from "react-redux";
import { DropdownButton, Dropdown } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { getAllVTOProduct, getBrands } from "../../features/vto/vtoSlice";
import { encodeId, get2DImage, get3DImage } from "../../utils/common";
import { LANDMARK } from "../../tryon/lib/landmarks";

import "./virtual_try_on.css";

const STATUS_LABEL = {
  "requesting-camera": "Waiting for camera permission…",
  "loading-model": "Loading face model…",
  "no-face":
    "Looking for your face — make sure it's lit and facing the camera.",
};

/**
 * Live virtual try-on: the webcam, a tracked face, and a pair of glasses
 * anchored to it. Renders inside a modal overlay so it can be opened on top of
 * any screen.
 *
 * @param {object} props
 * @param {boolean} props.isOpen Tracking, the camera and the WebGL context all
 * exist only while this is true.
 * @param {() => void} [props.onClose] Called when the user closes the modal.
 */
export default function VirtualTryOn({
  isOpen = true,
  onClose,
  category,
  brand,
  size,
  total_measurements,
  glbModal,
}) {
  const {
    videoRef,
    landmarksRef,
    onDetectRef,
    status,
    error,
    sourceSize,
  } = useWebcamTracking(isOpen);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [containerRef, containerSize] = useElementSize();
  const [frame, setFrame] = useState(null);
  const [pd, setPd] = useState(null);

  const [showSimilar, setShowSimilar] = useState(
    () => typeof window !== "undefined" && window.innerWidth > 767,
  );

  const [selectedBrand, setSelectedBrand] = useState("All Brands");
  const [selectedBrandId, setSelectedBrandId] = useState("");

  const [brandList, setBrandList] = useState([]);

  const { vtoProducts, status: vtoStatus, brands } = useSelector(
    (state) => state.vto,
  );
  const is_signin = useSelector((state) => state.auth.is_signin);

  const products = vtoProducts || [];

  useEffect(() => {
    if (glbModal) {
      setFrame({
        modelUrl: glbModal,
        calibration: {
          rotationY: 0,
          offsetY: -0.03,
          offsetZ: 0,
          widthRatio: 0.94,
        },
      });
    }
  }, [glbModal]);


  useEffect(() => {
    if (!isOpen) return;
    const categoryKey = category?.id || "";
    dispatch(getAllVTOProduct({ categoryKey, brand_id: selectedBrandId }));
  }, [selectedBrandId, isOpen, dispatch, category]);

  useEffect(() => {
    if (category?.id) {
      dispatch(getBrands({ categoryKey: category.id }));
    }
  }, [category]);

  useEffect(() => {
    if (brands.length > 0) {
      const list = brands.map((val) => {
        return { key: val?.id, value: val?.name };
      });
      setBrandList(list);
    } else {
      setBrandList([]);
    }
  }, [brands]);


  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen || status !== "tracking") return;
    let rafId;
    const tick = () => {
      const face = landmarksRef.current;
      if (face && face.length > LANDMARK.RIGHT_IRIS) {
        const leftIris = face[LANDMARK.LEFT_IRIS];
        const rightIris = face[LANDMARK.RIGHT_IRIS];
        const leftFace = face[LANDMARK.FACE_LEFT];
        const rightFace = face[LANDMARK.FACE_RIGHT];

        const pupilDx = rightIris.x - leftIris.x;
        const pupilDy = rightIris.y - leftIris.y;
        const pupilDist = Math.sqrt(pupilDx * pupilDx + pupilDy * pupilDy);

        const faceDx = rightFace.x - leftFace.x;
        const faceDy = rightFace.y - leftFace.y;
        const faceWidth = Math.sqrt(faceDx * faceDx + faceDy * faceDy);

        if (faceWidth > 0.01) {
          const AVG_FACE_WIDTH_MM = 145;
          const pdMm = Math.round((pupilDist / faceWidth) * AVG_FACE_WIDTH_MM);
          setPd((prev) => (prev !== pdMm ? pdMm : prev));
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [isOpen, status, landmarksRef]);

  // "no-face" still means the pipeline is up; the scene handles an absent face
  // on its own, and tearing it down over a blink would be worse than useless.
  const ready = status === "tracking" || status === "no-face";

  const handleTryOn = (image, productSize, productTotalMeasurements) => {
    const vtoImage = get3DImage(image);
    // console.warn("Try on clicked", {
    //   vtoImage,
    //   productSize,
    //   productTotalMeasurements,
    // });

    if (vtoImage) {
      setFrame({
        modelUrl: vtoImage,
        calibration: {
          rotationY: 0,
          offsetY: -0.03,
          offsetZ: 0,
          widthRatio: 0.94,
        },
      });
    }
  };

  const handleBuyNow = (productId) => {
    if (is_signin) {
      navigate(`/product-descrption/${encodeId(productId)}`);
    } else {
      navigate("/login-product-detail");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="vto-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Virtual try on"
    >
      <button
        type="button"
        className="vto-modal__backdrop"
        aria-label="Close virtual try on"
        onClick={onClose}
      />

      <div className="vto-modal__panel">
        <button
          type="button"
          className={`vto-modal__close ${showSimilar ? "is-similar-open" : ""}`}
          aria-label="Close virtual try on"
          onClick={onClose}
        >
          &times;
        </button>

        <div className="vto">
          <div ref={containerRef} className="vto-stage">
            <video ref={videoRef} playsInline muted className="vto-video" />

            {ready && sourceSize && containerSize && (
              <TryOnScene
                frame={frame}
                landmarksRef={landmarksRef}
                onDetectRef={onDetectRef}
                videoRef={videoRef}
                sourceSize={sourceSize}
                containerSize={containerSize}
                mirrored
                fit="cover"
              />
            )}

            {(status === "requesting-camera" || status === "loading-model") && (
              <div className="vto-overlay">
                <span className="vto-spinner" />
                <p>{STATUS_LABEL[status]}</p>
              </div>
            )}

            {status === "error" && (
              <div className="vto-overlay">
                <p className="vto-error-title">Camera unavailable</p>
                <p className="vto-error-body">{error}</p>
              </div>
            )}

            {status === "no-face" && (
              <p className="vto-pill">{STATUS_LABEL["no-face"]}</p>
            )}

            {pd && ready && <span className="vto-pd-badge">PD : {pd}</span>}
          </div>

          <button
            type="button"
            className={`vto-similar-toggle ${showSimilar ? "is-hidden" : ""}`}
            aria-expanded={showSimilar}
            aria-controls="vto-similar-panel"
            onClick={() => setShowSimilar((v) => !v)}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
            Show Similar Products
          </button>

          <div
            id="vto-similar-panel"
            className={`vto-similar-panel ${
              showSimilar ? "vto-similar-panel--open" : ""
            }`}
          >
            <div className="vto-right-header">
              <h3 className="vto-similar-heading">Similar Products</h3>
              <button
                type="button"
                className="vto-similar-close"
                aria-label="Close similar products"
                onClick={() => setShowSimilar(false)}
              >
                &times;
              </button>
            </div>

            <div className="vto-similar-filter">
              <DropdownButton
                id="vto-similar-dropdown"
                title={
                  <span
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ flexShrink: 0 }}
                    >
                      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                    </svg>
                    {selectedBrand}
                  </span>
                }
                className="vto-custom-dropdown"
              >
                <Dropdown.Item
                  as="button"
                  key="all"
                  onClick={() => {
                    setSelectedBrand("All Brands");
                    setSelectedBrandId("");
                  }}
                  className={`dropdown-radio-item ${
                    selectedBrand === "All Brands" ? "active" : ""
                  }`}
                >
                  <div className="radio-option">
                    <input
                      type="radio"
                      name="vto-brand"
                      checked={selectedBrand === "All Brands"}
                      readOnly
                    />
                    <label>All Brands</label>
                  </div>
                </Dropdown.Item>

                {brandList.map((brand) => (
                  <Dropdown.Item
                    as="button"
                    key={brand.key}
                    onClick={() => {
                      setSelectedBrand(brand.value);
                      setSelectedBrandId(brand.key);
                    }}
                    className={`dropdown-radio-item ${
                      selectedBrand === brand.value ? "active" : ""
                    }`}
                  >
                    <div className="radio-option">
                      <input
                        type="radio"
                        name="vto-brand"
                        checked={selectedBrand === brand.value}
                        readOnly
                      />
                      <label>{brand.value}</label>
                    </div>
                  </Dropdown.Item>
                ))}
              </DropdownButton>
            </div>

            <div className="vto-similar-list">
              {vtoStatus === "loading" ? (
                <div className="vto-similar-empty">
                  Loading products, please wait&hellip;
                </div>
              ) : products?.length > 0 ? (
                <div className="vto-similar-grid">
                  {products?.map((product) => {
                    const votImage = get2DImage(product?.Product_Images);

                    return (
                      <div
                        key={product.id}
                        className="vto_card"
                        style={{ cursor: "pointer" }}
                      >
                        <Link
                          to={`/product-descrption/${encodeId(product?.id)}`}
                        >
                          <div className="vto-similar-card-img">
                            <img
                              src={
                                process.env.REACT_APP_IMAGE_URL + product?.image
                              }
                              alt={product?.name}
                            />
                          </div>

                          <div className="vto-similar-card-body">
                            <p className="vto-similar-card-type">
                              {product?.p_category?.name}
                            </p>
                            <p className="vto-similar-card-name">
                              {product?.name}
                            </p>
                            <p className="vto-similar-card-price">
                              ₹{product?.price}-
                              <span className="vto-similar-card-oldprice">
                                ₹{product?.mrp}
                              </span>
                            </p>
                          </div>
                        </Link>
                        <div
                          className="vto-similar-card-btns"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            className="vto-similar-tryon"
                            onClick={() =>
                              handleTryOn(
                                product?.Product_Images,
                                product?.size,
                                product?.total_measurements,
                              )
                            }
                          >
                            Try On
                          </button>
                          <button
                            className="vto-similar-buynow"
                            onClick={() => handleBuyNow(product?.id)}
                          >
                            {" "}
                            Buy Now
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="vto-similar-empty">
                  <p>No products found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
